import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { fetchAllInProgressProjects, fetchInProgressFolders } from "@store/thunks/devnagriValidationAdminThunk";
import { setSelectedAssignedProjectId } from "@store/slices/devnagriValidatorAdminSlice";
import { restrictSpecialCharacters, ROLE_ROUTES } from "@utils/constants";
import { toast } from "react-toastify";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ProjectDetailTableDevnagriValidatorAdmin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const { assignedProjects = [] } = useSelector((store) => store.devnagriValidatorAdmin);
  const [inputValue, setInputValue] = useState("");
  const debouncedSearchQuery = useDebounce(inputValue.toLowerCase(), 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
 const [hasNextPage, setHasNextPage] = useState(true);
   const [allProjects, setAllProjects] = useState([]);
   const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setInitialLoading(true);
        const res = await dispatch(
          fetchAllInProgressProjects(debouncedSearchQuery, pageIndex, pageSize)
        );
        if (Array.isArray(res)) {
            setAllProjects(res)
            if (res.length < pageSize) {
              setHasNextPage(false);
            } else {
              setHasNextPage(true);
            }
          } else {
            setAllProjects([]);
            setHasNextPage(false);
          }
      } catch (error) {
        console.error("[ProjectDetailTableDevnagriValidatorAdmin] Error fetching projects:", error);
        setError("Failed to fetch projects. Please try again.");
        toast.error("Failed to fetch projects.");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchProjects();
  }, [dispatch, debouncedSearchQuery, pageIndex, pageSize]);

  const handleProjectClick = async ({ row }) => {
    try {
      setLoading(true);
      const projectId = row.original.projectId;
      if (!projectId) {
        throw new Error("Invalid project ID");
      }
      dispatch(setSelectedAssignedProjectId(projectId));
      await dispatch(fetchInProgressFolders(projectId));
      navigate(`${ROLE_ROUTES["DEVANAGARI ADMIN"]}/folders`);
    } catch (error) {
      console.error("[ProjectDetailTableDevnagriValidatorAdmin] Error handling project click:", error);
      setError("Failed to fetch folders for the selected project.");
      toast.error("Failed to fetch folders.");
    } finally {
      setLoading(false);
    }
  };

  const projectColumns = [
    {
      id: "serial_number",
      header: "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "projectName",
      header: "Project Name",
      accessorKey: "projectName",
      cell: ({ row }) => (
        <button
          onClick={() => handleProjectClick({ row })}
          className={`text-[#ec7026] hover:text-[#d85d1a] hover:underline font-['Semantic/Strong'] ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          disabled={loading}
        >
          {row.original.projectName || "N/A"}
        </button>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      cell: ({ row }) => (
        <div className="max-w-md">
          {row.original.description || "No description available"}
        </div>
      ),
    },
    {
      id: "createdAt",
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => {
        const uploadDate = row.original?.createdAt;
        if (!uploadDate) return "N/A";
        try {
          const date = new Date(uploadDate);
          return (
            <div className="text-center">
              <div className="text-gray-600">
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return uploadDate;
        }
      },
    },
    {
      id: "deadline",
      header: "Deadline",
      accessorKey: "deadline",
      cell: ({ row }) => {
        const deadline = row.original?.deadline;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div className="text-center">
              <div
                className={
                  isOverdue ? "text-red-600" : isApproaching ? "text-amber-600" : "text-gray-600"
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
    },
  ];

  // Client-side filtering as a fallback
  const filteredProjects = debouncedSearchQuery
    ? assignedProjects.filter(
        (project) =>
          project.status !== "DELETED" &&
          (project.projectName?.toLowerCase().includes(debouncedSearchQuery) ||
           project.description?.toLowerCase().includes(debouncedSearchQuery))
      )
    : assignedProjects.filter((project) => project.status !== "DELETED");

  if (initialLoading || loading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-full"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p className="font-['Semantic/Strong']">{error}</p>
            <p className="font-['Semantic/Strong'] mt-2">Please try again later or contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center mb-6">
        <div className="relative w-full sm:w-64">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search projects..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>


      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="w-full overflow-x-auto">
          {allProjects.length === 0 ? (
            <div className="p-6">
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
                <p className="font-['Semantic/Strong']">No projects found.</p>
                <p className="font-['Semantic/Strong'] mt-2">
                  No projects match the search criteria or you have not been assigned any projects.
                </p>
              </div>
            </div>
          ) : (
            <GenericTable
              data={[...allProjects].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
              columns={projectColumns}
              showPagination={true}
                  manualPagination={true}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  hasNextPage={hasNextPage}
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
              emptyMessage="No devnagri validator admin projects available"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailTableDevnagriValidatorAdmin;